import { classes } from "polytype";
import type {
  ConversationAggregateReference,
  ConversationCreateMessageRequestModelData,
  ConversationCreateRequest,
  ConversationData,
  ConversationListItemData,
  ConversationListQueryData,
  ConversationRelatedAggregateReference,
  ConversationShareableAggregateReference,
} from "./types.js";
import {
  AggregateMetaData,
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import type { ConversationCategoryReferenceType } from "../ConversationCategory/index.js";
import { ConversationCategory } from "../ConversationCategory/index.js";
import {
  ConversationUser,
  ConversationUserListQuery,
} from "../ConversationUser/index.js";
import { DateTime } from "luxon";
import { ConversationMessageFileAttachmentAccessTokenProvider } from "../ConversationMessage/ConversationMessageFileAttachmentAccessTokenProvider.js";
import type { FileAccessTokenProvider } from "../../file/index.js";
import { File } from "../../file/index.js";
import { ConversationStatusUpdate } from "../ConversationStatusUpdate/index.js";
import { ConversationServiceRequest } from "../ConversationServiceRequest/index.js";
import { ConversationMessage } from "../ConversationMessage/index.js";
import { AggregateReference } from "../../base/AggregateReference.js";

export class Conversation extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "conversation",
    "conversation",
  );
  public readonly fileAccessTokenProvider: FileAccessTokenProvider;
  public readonly users: ConversationUserListQuery;

  public constructor(id: string) {
    super(id);
    this.fileAccessTokenProvider =
      new ConversationMessageFileAttachmentAccessTokenProvider(this);
    this.users = new ConversationUserListQuery(this);
  }

  public static ofId(id: string) {
    return new Conversation(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.conversation.find(id);
    if (data) {
      return new ConversationDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const conversation = await this.find(id);
    assertObjectFound(conversation, "conversation", id);
    return conversation;
  });

  public findDetailed = provideReact(
    () => Conversation.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ConversationDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Conversation.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ConversationDetailed>>;

  public static query(query: ConversationListQueryData = {}) {
    return new ConversationListQuery(query);
  }

  public static async create(data: ConversationCreateRequest) {
    const response = await config.behaviors.conversation.create({
      ...data,
      relatedTo: data.relatedTo as ConversationRelatedAggregateReference,
      sharedWith: data.sharedWith as ConversationShareableAggregateReference,
    });

    return Conversation.ofId(response.id);
  }

  public async updateCategory(categoryId: string) {
    await config.behaviors.conversation.update(this.id, { categoryId });
  }

  public async updateRelation(relatedTo: ConversationAggregateReference) {
    await config.behaviors.conversation.update(this.id, {
      relatedTo: relatedTo as ConversationRelatedAggregateReference,
    });
  }

  public async updateTitle(title: string) {
    await config.behaviors.conversation.update(this.id, { title });
  }

  public async createMessage(data: ConversationCreateMessageRequestModelData) {
    const { files, ...restData } = data;

    const uploadedFiles = await Promise.all(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      [...(files ?? [])].map((f) =>
        File.upload(f, this.fileAccessTokenProvider),
      ),
    );

    await config.behaviors.conversation.createMessage(this.id, {
      ...restData,
      fileIds: uploadedFiles.map((f) => f.id),
    });
  }

  public static listCategories = provideReact(async () => {
    const response = await config.behaviors.conversation.listCategories();
    return response
      .map((c) => new ConversationCategory(c))
      .sort((a, b) => a.translatedName.localeCompare(b.translatedName));
  });

  public static listRelatedCategories = provideReact(
    async (relationType: ConversationCategoryReferenceType[number]) => {
      const categories = await this.listCategories();
      return categories.filter((c) =>
        c.data.referenceType.find((t) => t === relationType),
      );
    },
  );

  public async close() {
    await config.behaviors.conversation.setConversationStatus(
      this.id,
      "closed",
    );
  }

  public listMessages = provideReact(async () => {
    const data = await config.behaviors.conversation.listMessages(this.id);
    return data.map((d) =>
      d.type === "STATUS_UPDATE"
        ? new ConversationStatusUpdate(d)
        : d.type === "SERVICE_REQUEST"
          ? new ConversationServiceRequest(d)
          : new ConversationMessage(d),
    );
  }, [this.id]);

  public listMembers = provideReact(async () => {
    const data = await config.behaviors.conversation.getMembers(this.id);
    return data.map((d) => new ConversationUser(d));
  }, [this.id]);
}

class ConversationCommon extends classes(
  DataModel<ConversationData | ConversationListItemData>,
  Conversation,
) {
  public readonly title: string;
  public readonly mainUser: ConversationUser;
  public readonly createdAt: DateTime;
  public readonly createdBy?: ConversationUser;
  public readonly relation?: AggregateReference;
  public readonly lastMessageAt?: DateTime;
  public readonly lastMessageBy?: ConversationUser;
  public readonly category?: ConversationCategory;
  public readonly isOpen: boolean;
  public readonly sharedWith?: AggregateReference;
  public readonly shortId: string;

  public constructor(data: ConversationData | ConversationListItemData) {
    super([data], [data.conversationId]);
    this.title = data.title;
    this.mainUser = new ConversationUser(data.mainUser);
    this.relation = data.relatedTo
      ? new AggregateReference(data.relatedTo)
      : undefined;
    this.createdAt = DateTime.fromISO(data.createdAt);
    if (data.createdBy) {
      this.createdBy = new ConversationUser(data.createdBy);
    }
    if (data.lastMessage) {
      this.lastMessageAt = DateTime.fromISO(data.lastMessage.createdAt);
    }
    if (data.lastMessage?.createdBy) {
      this.lastMessageBy = new ConversationUser(data.lastMessage.createdBy);
    }
    this.category = data.category
      ? new ConversationCategory(data.category)
      : undefined;
    this.isOpen = data.status !== "closed";
    this.sharedWith = data.sharedWith
      ? new AggregateReference(data.sharedWith)
      : undefined;
    this.shortId = data.shortId;
  }
}

export class ConversationDetailed extends classes(
  ConversationCommon,
  DataModel<ConversationData>,
) {
  public constructor(data: ConversationData) {
    super([data], [data]);
  }
}

export class ConversationListItem extends classes(
  ConversationCommon,
  DataModel<ConversationListItemData>,
) {
  public constructor(data: ConversationListItemData) {
    super([data], [data]);
  }
}

export class ConversationListQuery extends ListQueryModel<ConversationListQueryData> {
  public constructor(query: ConversationListQueryData = {}) {
    super(query);
  }

  public refine(query: ConversationListQueryData) {
    return new ConversationListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.conversation.list(this.query);
    return new ConversationList(
      this.query,
      items.map((d) => new ConversationListItem(d)),
    );
  }, [this.queryId, "conversation"]);
}

export class ConversationList extends classes(
  ConversationListQuery,
  ListDataModel<ConversationListItem>,
) {
  public constructor(
    query: ConversationListQueryData,
    conversations: ConversationListItem[],
  ) {
    super([query], [conversations, conversations.length]);
  }
}
